/**
 * Lindy - Product Media Gallery Component
 *
 * Custom product media gallery with vertical thumbnail navigation (desktop)
 * and swipeable gallery with arrow navigation (mobile/desktop).
 *
 * Features:
 * - Vertical thumbnail sidebar with scroll snap
 * - Drag/swipe support for main gallery
 * - Keyboard navigation (arrow keys)
 * - Variant change integration
 * - Auto-disable navigation at boundaries
 */
class ProductMediaGalleryLindy extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;

    /** @type {HTMLButtonElement[]} */
    this.thumbnails = [];

    /** @type {HTMLElement[]} */
    this.mainSlides = [];

    /** @type {HTMLElement | null} */
    this.thumbnailsScroll = null;

    /** @type {HTMLButtonElement | null} */
    this.navUp = null;

    /** @type {HTMLButtonElement | null} */
    this.navDown = null;

    /** @type {HTMLButtonElement | null} */
    this.mobileNavPrev = null;

    /** @type {HTMLButtonElement | null} */
    this.mobileNavNext = null;

    /** @type {HTMLElement | null} */
    this.mainImageContainer = null;

    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.hasDragged = false;
    this.dragThreshold = 5;
  }

  connectedCallback() {
    this.thumbnails = Array.from(this.querySelectorAll(".thumbnail-item"));
    this.mainSlides = Array.from(this.querySelectorAll(".main-image-slide"));
    this.thumbnailsScroll = this.querySelector(".thumbnails-scroll");
    this.navUp = this.querySelector(".gallery-nav-up");
    this.navDown = this.querySelector(".gallery-nav-down");
    this.mobileNavPrev = this.querySelector(".mobile-nav-prev");
    this.mobileNavNext = this.querySelector(".mobile-nav-next");
    this.mainImageContainer = this.querySelector(".main-image-container");

    this.init();
  }

  /**
   * Initialize the gallery component.
   * Finds active slide and sets up event listeners.
   */
  init() {
    this.mainSlides.forEach((slide, index) => {
      if (slide.classList.contains("active")) {
        this.currentIndex = index;
      }
    });

    this.updateNavigationButtons();
    this.attachEventListeners();
    this.setupDragSwipe();
    this.setupVariantChangeListener();
  }

  /**
   * Attach event listeners for thumbnails, navigation buttons, and keyboard.
   */
  attachEventListeners() {
    this.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener("click", () => {
        this.showSlide(index);
      });
    });

    if (this.navUp) {
      this.navUp.addEventListener("click", () => {
        this.showSlide(this.currentIndex - 1);
      });
    }

    if (this.navDown) {
      this.navDown.addEventListener("click", () => {
        this.showSlide(this.currentIndex + 1);
      });
    }

    if (this.mobileNavPrev) {
      this.mobileNavPrev.addEventListener("click", () => {
        this.showSlide(this.currentIndex - 1);
      });
    }

    if (this.mobileNavNext) {
      this.mobileNavNext.addEventListener("click", () => {
        this.showSlide(this.currentIndex + 1);
      });
    }

    document.addEventListener("keydown", (e) => {
      if (!this.contains(document.activeElement)) return;

      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        this.showSlide(this.currentIndex - 1);
      } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        this.showSlide(this.currentIndex + 1);
      }
    });
  }

  /**
   * Show a specific slide by index.
   *
   * @param {number} index - The index of the slide to show.
   */
  showSlide(index) {
    if (index < 0) index = 0;
    if (index >= this.mainSlides.length) index = this.mainSlides.length - 1;

    this.currentIndex = index;

    this.mainSlides.forEach((slide, i) => {
      slide.classList.toggle("active", i === index);
    });

    this.thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle("active", i === index);
    });

    this.updateNavigationButtons();
    this.scrollThumbnailIntoView(index);
  }

  /**
   * Update navigation button states based on current slide position.
   * Disables prev buttons at first slide and next buttons at last slide.
   */
  updateNavigationButtons() {
    const isFirstSlide = this.currentIndex === 0;
    const isLastSlide = this.currentIndex === this.mainSlides.length - 1;

    if (this.navUp) {
      this.navUp.disabled = isFirstSlide;
    }

    if (this.navDown) {
      this.navDown.disabled = isLastSlide;
    }

    if (this.mobileNavPrev) {
      this.mobileNavPrev.disabled = isFirstSlide;
    }

    if (this.mobileNavNext) {
      this.mobileNavNext.disabled = isLastSlide;
    }
  }

  /**
   * Scroll the thumbnail at given index into view if not visible.
   *
   * @param {number} index - The index of the thumbnail to scroll into view.
   */
  scrollThumbnailIntoView(index) {
    if (!this.thumbnails[index] || !this.thumbnailsScroll) return;

    const thumbnail = this.thumbnails[index];
    const container = this.thumbnailsScroll;

    const thumbnailTop = thumbnail.offsetTop;
    const containerScrollTop = container.scrollTop;
    const containerHeight = container.offsetHeight;
    const thumbnailHeight = thumbnail.offsetHeight;

    const thumbnailBottom = thumbnailTop + thumbnailHeight;
    const containerBottom = containerScrollTop + containerHeight;

    let scrollTo = containerScrollTop;

    if (thumbnailTop < containerScrollTop) {
      scrollTo = thumbnailTop;
    } else if (thumbnailBottom > containerBottom) {
      scrollTo = thumbnailBottom - containerHeight;
    }

    if (scrollTo !== containerScrollTop) {
      container.scrollTo({
        top: scrollTo,
        behavior: "smooth",
      });
    }
  }

  /**
   * Setup listener for Shopify variant change events.
   * Automatically switches to the variant's featured media when changed.
   */
  setupVariantChangeListener() {
    if (typeof window.Shopify !== "undefined") {
      document.addEventListener("variant:change", (event) => {
        if (!(event instanceof CustomEvent)) return;

        const variant = event.detail?.variant;
        if (variant && variant.featured_media) {
          const mediaId = variant.featured_media.id;
          const index = this.mainSlides.findIndex((slide) => slide.dataset.mediaId == mediaId);
          if (index !== -1) {
            this.showSlide(index);
          }
        }
      });
    }
  }

  /**
   * Setup drag/swipe event listeners for main gallery.
   * Supports both touch (mobile) and mouse (desktop) events.
   */
  setupDragSwipe() {
    if (!this.mainImageContainer) return;

    this.mainImageContainer.addEventListener(
      "touchstart",
      (e) => {
        if (e.target instanceof Element && e.target.closest(".product-media-container__zoom-button")) return;

        const touch = e.changedTouches[0];
        if (touch) {
          this.startDrag(touch.clientX);
        }
      },
      { passive: true }
    );

    this.mainImageContainer.addEventListener(
      "touchmove",
      (e) => {
        if (this.isDragging) {
          const touch = e.changedTouches[0];
          if (touch) {
            this.drag(touch.clientX);
          }
        }
      },
      { passive: true }
    );

    this.mainImageContainer.addEventListener(
      "touchend",
      () => {
        this.endDrag();
      },
      { passive: true }
    );

    this.mainImageContainer.addEventListener("mousedown", (e) => {
      if (e.target instanceof Element && e.target.closest(".product-media-container__zoom-button")) return;

      this.startDrag(e.clientX);
      e.preventDefault();
    });

    this.mainImageContainer.addEventListener("mousemove", (e) => {
      if (this.isDragging) {
        this.drag(e.clientX);
      }
    });

    this.mainImageContainer.addEventListener("mouseup", () => {
      this.endDrag();
    });

    this.mainImageContainer.addEventListener("mouseleave", () => {
      this.endDrag();
    });
  }

  /**
   * Start drag/swipe gesture.
   *
   * @param {number} x - The starting X coordinate.
   */
  startDrag(x) {
    this.isDragging = true;
    this.hasDragged = false;
    this.startX = x;
    this.currentX = x;
  }

  /**
   * Handle drag/swipe movement.
   *
   * @param {number} x - The current X coordinate.
   */
  drag(x) {
    if (!this.isDragging) return;

    this.currentX = x;
    const diff = this.currentX - this.startX;

    if (!this.hasDragged && Math.abs(diff) > this.dragThreshold) {
      this.hasDragged = true;
    }
  }

  /**
   * End drag/swipe gesture and determine if slide should change.
   * Changes slide if drag distance exceeds threshold (50px).
   */
  endDrag() {
    if (!this.isDragging) return;

    const diff = this.currentX - this.startX;
    const threshold = 50;

    this.isDragging = false;

    if (Math.abs(diff) > threshold) {
      if (diff > 0 && this.currentIndex > 0) {
        this.showSlide(this.currentIndex - 1);
      } else if (diff < 0 && this.currentIndex < this.mainSlides.length - 1) {
        this.showSlide(this.currentIndex + 1);
      }
    }

    setTimeout(() => {
      this.hasDragged = false;
    }, 100);
  }

  /**
   * Clean up event listeners when component is disconnected.
   */
  disconnectedCallback() {
    // Event listeners are automatically removed when element is disconnected
  }
}

if (!customElements.get("product-media-gallery-lindy")) {
  customElements.define("product-media-gallery-lindy", ProductMediaGalleryLindy);
}
